import {
  GOOGLE_MAPS_API_BASE,
  GOOGLE_PLACES_API_BASE,
} from "@/config/googleMaps";
import {
  normalizeGooglePlace,
  type GooglePlaceDetailsResult,
} from "@/utils/places/normalizeGooglePlace";
import { resolveEasyModePlace } from "@/utils/places/resolveEasyModePlace";
import type { DailyCommuteLocation } from "@/types/dailyCommuteLocation";
import type { EasyModePlaceSelection } from "@/types/easyModePlace";

const PLACE_DETAILS_FIELDS =
  "place_id,name,geometry,formatted_address,address_components,types";

const AUTOCOMPLETE_LANGUAGE = "en";
const MAX_PREDICTIONS = 15;

interface GeocodeResponse {
  results?: GooglePlaceDetailsResult[];
  status: string;
  error_message?: string;
}

export interface PlacePrediction {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

interface AutocompleteResponse {
  predictions?: Array<{
    description: string;
    place_id: string;
    structured_formatting?: {
      main_text?: string;
      secondary_text?: string;
    };
  }>;
  status: string;
  error_message?: string;
}

interface PlaceDetailsResponse {
  result?: GooglePlaceDetailsResult;
  status: string;
  error_message?: string;
}

export type LocationSearchMode = "origin" | "destination";

/**
 * Creates a unique v4 UUID session token for Google Places Autocomplete sessions.
 * Using session tokens groups all keystrokes during a user search into $0 autocomplete calls,
 * charging only for the single terminating Place Details call (40%-80% cost savings).
 */
export function createPlaceSessionToken(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Autocomplete — Easy Mode / Find Ride parity with ChaloApp mobile.
 * types=establishment|geocode, language=en, optional India restrict.
 */
function buildAutocompleteRequestUrl(
  input: string,
  restrictToCountryCode?: string,
  sessionToken?: string | null
): string {
  const params = new URLSearchParams({
    input,
    language: AUTOCOMPLETE_LANGUAGE,
    types: "establishment|geocode",
  });

  if (sessionToken) {
    params.set("sessiontoken", sessionToken);
  }

  if (restrictToCountryCode) {
    const countryCode = restrictToCountryCode.toUpperCase();
    params.set("components", `country:${countryCode}`);
    params.set("region", countryCode.toLowerCase());
  }

  return `${GOOGLE_PLACES_API_BASE}/autocomplete/json?${params.toString()}`;
}

function buildPlaceDetailsRequestUrl(
  placeId: string,
  sessionToken?: string | null
): string {
  const params = new URLSearchParams({
    place_id: placeId,
    fields: PLACE_DETAILS_FIELDS,
    language: AUTOCOMPLETE_LANGUAGE,
  });

  if (sessionToken) {
    params.set("sessiontoken", sessionToken);
  }

  return `${GOOGLE_PLACES_API_BASE}/details/json?${params.toString()}`;
}

async function fetchPlaceDetailsResult(
  placeId: string,
  sessionToken?: string | null
): Promise<GooglePlaceDetailsResult> {
  const url = buildPlaceDetailsRequestUrl(placeId, sessionToken);
  const res = await fetch(url);

  if (!res.ok) {
    throw new Error(`Place details request failed: ${res.status}`);
  }

  const json = (await res.json()) as PlaceDetailsResponse;
  if (json.status !== "OK" || !json.result) {
    throw new Error(json.error_message ?? `Place details status: ${json.status}`);
  }

  const lat = json.result.geometry?.location?.lat;
  const lng = json.result.geometry?.location?.lng;
  if (typeof lat !== "number" || typeof lng !== "number") {
    throw new Error("Place details missing geometry.location");
  }

  return json.result;
}

export async function fetchPlacePredictions(
  input: string,
  _mode: LocationSearchMode = "origin",
  restrictToCountryCode?: string,
  sessionToken?: string | null
): Promise<PlacePrediction[]> {
  const trimmed = input.trim();
  if (trimmed.length < 2) return [];

  const url = buildAutocompleteRequestUrl(trimmed, restrictToCountryCode, sessionToken);
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Autocomplete request failed: ${res.status}`);
  }

  const json = (await res.json()) as AutocompleteResponse;
  if (json.status !== "OK" && json.status !== "ZERO_RESULTS") {
    throw new Error(json.error_message ?? `Autocomplete status: ${json.status}`);
  }

  return (json.predictions ?? []).slice(0, MAX_PREDICTIONS).map((prediction) => ({
    placeId: prediction.place_id,
    description: prediction.description,
    mainText:
      prediction.structured_formatting?.main_text ?? prediction.description,
    secondaryText: prediction.structured_formatting?.secondary_text ?? "",
  }));
}

/** Commute / generic consumers — basic normalize (backward compatible). */
export async function resolvePlaceDetails(
  placeId: string,
  sessionToken?: string | null
): Promise<DailyCommuteLocation> {
  const result = await fetchPlaceDetailsResult(placeId, sessionToken);
  return normalizeGooglePlace(result);
}

/**
 * Find Ride / Easy Mode — full city + area (sc / slc) extraction matching mobile.
 */
export async function resolveEasyModePlaceDetails(
  placeId: string,
  predictionMainText?: string | null,
  sessionToken?: string | null
): Promise<EasyModePlaceSelection> {
  const result = await fetchPlaceDetailsResult(placeId, sessionToken);
  return resolveEasyModePlace(result, { predictionMainText });
}

export async function resolveLocationFromCoordinates(
  lat: number,
  lng: number
): Promise<DailyCommuteLocation> {
  const params = new URLSearchParams({
    latlng: `${lat},${lng}`,
    language: AUTOCOMPLETE_LANGUAGE,
  });

  const url = `${GOOGLE_MAPS_API_BASE}/geocode/json?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Reverse geocode request failed: ${res.status}`);
  }

  const json = (await res.json()) as GeocodeResponse;
  const result = json.results?.[0];
  if (json.status !== "OK" || !result?.place_id) {
    throw new Error(json.error_message ?? `Reverse geocode status: ${json.status}`);
  }

  if (
    typeof result.geometry?.location?.lat !== "number" ||
    typeof result.geometry?.location?.lng !== "number"
  ) {
    throw new Error("Reverse geocode missing geometry.location");
  }

  return normalizeGooglePlace(result);
}

/** My location → Easy Mode place selection (Find Ride). */
export async function resolveEasyModeLocationFromCoordinates(
  lat: number,
  lng: number
): Promise<EasyModePlaceSelection> {
  const params = new URLSearchParams({
    latlng: `${lat},${lng}`,
    language: AUTOCOMPLETE_LANGUAGE,
  });

  const url = `${GOOGLE_MAPS_API_BASE}/geocode/json?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Reverse geocode request failed: ${res.status}`);
  }

  const json = (await res.json()) as GeocodeResponse;
  const result = json.results?.[0];
  if (json.status !== "OK" || !result?.place_id) {
    throw new Error(json.error_message ?? `Reverse geocode status: ${json.status}`);
  }

  return resolveEasyModePlace(result);
}

